// @ts-strict-ignore
import type { Entry, EntryCollection } from "contentful";
import get from "lodash/get";
import type { LegalArticleListContentModel } from "types/contentful";
import type { LegalArticle, LegalArticleList } from "types/legal";
import { getHeaders } from "./tableOfContents";

export const buildPageMeta = (articleFields) => {
  const pageMeta = get(articleFields, "pageMeta", {});
  const url = get(articleFields, "slug", "");
  return {
    title: get(pageMeta, "fields.metaTitle", "Legal Documentation & Reference"),
    description: get(
      pageMeta,
      "fields.metaDescription",
      "Gemini complies with all applicable laws and regulations in each jurisdiction in which it operates. View various Gemini legal documentation and references here."
    ),
    url
  };
};

export const buildHeaders = (content, contentPartTwo) => {
  if (contentPartTwo && content)
    return [...getHeaders(content), ...getHeaders(contentPartTwo)];
  if (content) return getHeaders(content);
  if (contentPartTwo) return getHeaders(contentPartTwo);
};

export const buildAttachedFiles = (attachedFiles) => {
  const files = attachedFiles.filter(({ fields }) => fields.file?.url);
  return files.map(({ fields }) => ({
    url: fields.file.url,
    title: get(fields, "title", "")
  }));
};

export const decorateLegalArticle = (
  article: Entry<LegalArticle>
): LegalArticle => {
  const articleFields = get(article, "fields", {});
  return {
    name: get(articleFields, "name", ""),
    slug: get(articleFields, "slug", ""),
    lastUpdated: get(articleFields, "lastUpdated", ""),
    content: get(articleFields, "content.content", null),
    contentPartTwo: get(articleFields, "contentPartTwo.content", null),
    attachedPDFs: buildAttachedFiles(get(articleFields, "attachedPDFs", [])),
    footnotes: get(articleFields, "footnotes", ""),
    pageMeta: buildPageMeta(articleFields)
  };
};

export const decorateLegalArticles = (
  articles: Entry<LegalArticleListContentModel>,
  type: string
): LegalArticleList => {
  const articleListFields = get(articles, "fields", {});
  const legalArticles = get(articleListFields, "legalArticles", []);

  const decoratedArticles = legalArticles.map((article) => {
    const articleFields = get(article, "fields", {});
    return {
      name: get(articleFields, "name", ""),
      slug: get(articleFields, "slug", ""),
      lastUpdated: get(articleFields, "lastUpdated", ""),
      pageMeta: buildPageMeta(articleFields)
    };
  });
  return {
    type,
    legalArticles: decoratedArticles
  };
};

export const decorateLegalSections = (
  sections: EntryCollection<LegalArticleListContentModel>
): LegalArticleList[] => {
  const fetchedSections: Entry<LegalArticleListContentModel>[] = get(
    sections,
    "items",
    []
  );
  return fetchedSections.map((section) =>
    decorateLegalArticles(section, section.fields.description)
  );
};
